import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListOl } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./OrderAdherenceSection.module.css";

const LEVEL_LABEL = {
  na_ordem: "na ordem",
  pequena_troca: "pequena troca",
  reordenada: "reordenada",
};

function fmtDay(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit" }).format(new Date(iso));
}

function dotClass(s) {
  if (!s.available) return styles.dotNone;
  if (s.level === "reordenada") return styles.dotMoved;
  if (s.level === "pequena_troca") return styles.dotSmall;
  return styles.dotOk;
}

function dotLabel(s) {
  const base = `${fmtDay(s.finished_at)}${s.plan_title ? `, ${s.plan_title}` : ""}`;
  if (!s.available) return `${base}: sem ordem registrada`;
  return `${base}: ${LEVEL_LABEL[s.level]}${s.started_with_other ? ", começou por outro exercício" : ""}`;
}

/**
 * Seção "Aderência à prescrição" do acompanhamento do aluno (só professor/admin).
 * Mostra, nas últimas sessões, quão perto da ordem prescrita o aluno treina e
 * os padrões que valem uma conversa. Informativo: o aluno não informa o motivo
 * de treinar fora de ordem (pode ser aparelho ocupado), então o texto é neutro.
 *
 * `Section` é o CollapsibleSection do perfil (passado por prop pra não
 * duplicar o componente). Nada é renderizado se o recurso estiver desligado
 * ou o aluno não tiver sessões pra comparar.
 */
export default function OrderAdherenceSection({ studentId, onOpenSession, Section }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!studentId) return undefined;
    let alive = true;
    setData(null);
    api.get(`/dashboard/professor/student/${studentId}/order-adherence`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ enabled: false }); });
    return () => { alive = false; };
  }, [studentId]);

  if (!data || !data.enabled || !data.sessions?.length) return null;
  const { counts, analyzed } = data;
  if (analyzed === 0 && counts.sem_dados === 0) return null;

  const badge = counts.reordenada > 0
    ? `${counts.reordenada} ${counts.reordenada === 1 ? "reordenada" : "reordenadas"}`
    : counts.pequena_troca > 0
      ? `${counts.pequena_troca} ${counts.pequena_troca === 1 ? "pequena troca" : "pequenas trocas"}`
      : (analyzed > 0 ? "na ordem" : null);
  // da mais antiga pra mais recente, como uma linha do tempo
  const timeline = [...data.sessions].reverse();
  const hasPatterns = !!data.starts_with || data.leaves_for_last.length > 0 || data.skipped_often.length > 0;
  const summary = [
    `${counts.na_ordem} na ordem`,
    `${counts.pequena_troca} com pequena troca`,
    `${counts.reordenada} ${counts.reordenada === 1 ? "reordenada" : "reordenadas"}`,
    counts.sem_dados > 0 ? `${counts.sem_dados} sem ordem registrada` : null,
  ].filter(Boolean).join(" · ");

  return (
    <Section title="Aderência à prescrição" icon={<FontAwesomeIcon icon={faListOl} />} defaultOpen={false} badge={badge}>
      <div className={styles.wrap}>
        <p className={styles.summary}>Últimas {data.sessions.length} sessões: {summary}</p>

        <ol className={styles.timeline} aria-label="Sessões, da mais antiga para a mais recente">
          {timeline.map((s) => (
            <li key={s.session_id}>
              <button
                type="button"
                className={`${styles.dot} ${dotClass(s)}`}
                aria-label={dotLabel(s)}
                title={dotLabel(s)}
                onClick={() => onOpenSession?.(s.session_id)}
              />
              <span className={styles.dotDate}>{fmtDay(s.finished_at)}</span>
            </li>
          ))}
        </ol>
        <p className={styles.legend}>
          <span className={`${styles.key} ${styles.dotOk}`} /> na ordem
          <span className={`${styles.key} ${styles.dotSmall}`} /> pequena troca
          <span className={`${styles.key} ${styles.dotMoved}`} /> reordenada
          <span className={`${styles.key} ${styles.dotNone}`} /> sem ordem registrada
        </p>

        {data.recurrent?.hit && (
          <p className={styles.recurrent} role="status">
            Padrão recorrente: em {data.recurrent.hits} das últimas {data.recurrent.of} sessões o treino saiu
            reordenado ou começou por outro exercício.
          </p>
        )}

        {hasPatterns ? (
          <ul className={styles.patterns}>
            {data.starts_with && (
              <li>
                Costuma começar por <strong>{data.starts_with.name}</strong>
                {data.starts_with.prescribed_first ? ` (prescrito: ${data.starts_with.prescribed_first})` : ""}
                {" "}em {data.starts_with.count} de {data.starts_with.of} sessões
              </li>
            )}
            {data.leaves_for_last.map((p) => (
              <li key={`last-${p.name}`}>
                Deixa pro fim: <strong>{p.name}</strong> em {p.count} de {p.of} sessões
              </li>
            ))}
            {data.skipped_often.map((p) => (
              <li key={`skip-${p.name}`}>
                Pula com frequência: <strong>{p.name}</strong> em {p.count} de {p.of} sessões
              </li>
            ))}
          </ul>
        ) : (
          analyzed >= 3 && <p className={styles.muted}>Nenhum padrão se repete nas últimas {analyzed} sessões.</p>
        )}

        <p className={styles.note}>A ordem pode refletir aparelho ocupado. O aluno não informa o motivo.</p>
      </div>
    </Section>
  );
}
